import type { AxiosError, AxiosInstance } from "axios";
import type { Envelope } from "../types/contract";
import type { GetInit, ObservedEnvelope } from "./gateway";
import { isWireEnvelope } from "./unwrap";

function headerOf(headers: unknown, name: string): string | null {
  if (!headers || typeof headers !== "object") return null;
  const readable = headers as {
    get?: (headerName: string) => unknown;
    [key: string]: unknown;
  };
  const value =
    readable.get?.(name) ?? readable[name] ?? readable[name.toLowerCase()];
  return typeof value === "string" ? value : null;
}

// Axios throws on non-2xx. The API returns the `{data,error,meta}` envelope
// on error too, so return that instead of a bare status-code message.
// HTTP 304 is a successful empty body when the caller asked to revalidate.
export async function withEnvelope<T>(
  p: Promise<{ data: unknown; status?: number; headers?: unknown }>,
): Promise<ObservedEnvelope<T>> {
  try {
    const res = await p;
    const etag = headerOf(res.headers, "etag");
    if (res.status === 304) {
      return {
        data: null,
        error: null,
        meta: { request_id: "", generated_at: "" },
        httpStatus: 304,
        etag,
      };
    }
    const env = res.data as Envelope<T>;
    return Object.assign(env, { httpStatus: res.status, etag });
  } catch (e) {
    const response = (e as AxiosError).response;
    const env = response?.data;
    if (isWireEnvelope(env)) {
      const status = response?.status;
      const etag = headerOf(response?.headers, "etag");
      if (typeof status === "number") {
        return Object.assign(env, {
          httpStatus: status,
          etag,
        }) as ObservedEnvelope<T>;
      }
      return Object.assign(env, { etag }) as ObservedEnvelope<T>;
    }
    throw e;
  }
}

export function orgGet<T>(
  api: AxiosInstance,
  path: string,
  params: Record<string, unknown>,
  headers: Record<string, string> | undefined,
  init?: GetInit,
): Promise<ObservedEnvelope<T>> {
  return withEnvelope(
    api.get(path, {
      params,
      headers: { ...headers, ...init?.headers },
      signal: init?.signal,
      validateStatus: init?.acceptNotModified
        ? (status: number) => (status >= 200 && status < 300) || status === 304
        : undefined,
    }),
  );
}
